import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { authService } from '../lib/auth';

interface Photo {
  id: number;
  image: string;
  thumbnail?: string;
  description?: string;
  created_at: string;
}

export const useChecklistStepPhotos = (stepRunId?: number) => {
  return useQuery({
    queryKey: ['checklistStepPhotos', stepRunId],
    queryFn: async (): Promise<Photo[]> => {
      if (!stepRunId) {
        return [];
      }
      
      const response = await apiClient.get<any>(`/api/photos/?checklist_step_run=${stepRunId}`);
      const photos = Array.isArray(response.data) ? response.data : (response.data.results || []);
      
      return photos;
    },
    enabled: !!stepRunId,
    retry: 1,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

export const useUploadChecklistStepPhotos = () => {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  
  return useMutation({
    mutationFn: async ({ 
      stepRunId, 
      files, 
      description,
      jobId,
      opportunityId 
    }: { 
      stepRunId: number; 
      files: File[]; 
      description?: string;
      jobId?: number;
      opportunityId?: number; 
    }) => {
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('checklist_step_run', stepRunId.toString());
      
      // Add job or opportunity ID (required by backend validation)
      if (jobId) {
        formData.append('job', jobId.toString());
      } else if (opportunityId) {
        formData.append('opportunity', opportunityId.toString());
      }
      
      if (description?.trim()) {
        formData.append('description', description.trim());
      }
      
      files.forEach((file) => {
        formData.append('image', file);
      });

      const accessToken = authService.getAccessToken();
      
      const response = await fetch('/api/photos/', {
        method: 'POST',
        headers: {
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(`Upload failed: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      
      const data = await response.json();

      setUploadProgress(100);
      return data;
    },
    onSuccess: (_, { stepRunId }) => {
      queryClient.invalidateQueries({ queryKey: ['checklistStepPhotos', stepRunId] });
    },
    onError: () => {
      setUploadProgress(0);
    },
  });
};